// @flow
import React, { memo, useEffect, useRef } from 'react'

import { RowLayoutContent } from 'app/common/layouts/RowLayout'
import FiltersPlainLayout, { Column } from 'app/common/layouts/FiltersPlainLayout'
import { FilterItem, useFilterData } from 'app/common/ui-components/filter'
import PlainHelperText from 'app/common/ui-next/plain-helper-text'

import ResetFilterLink from 'app/components/common/reports/ResetFilterLink'
import ReportSectionCSS from 'app/components/common/reports/ReportSectionCSS'

import orgsOrUserFilter, {
  flags as orgsOrUser,
} from 'app/components/common/reports/filters/orgsOrUser'
import customerSearchFilter, {
  flags as customerSearch,
} from 'app/components/common/reports/filters/customerSearch'
import orgTypesFilter from 'app/components/common/reports/filters/orgType'
import orgsFilter from 'app/components/common/reports/filters/org'
import buildingFilter from 'app/components/common/reports/filters/building'
import sellPointFilter from 'app/components/common/reports/filters/sellPoint'
import yearFilter from 'app/components/common/reports/filters/year'
import customerTypeFilter from 'app/components/common/reports/filters/customerType'
import classFilter from 'app/components/common/reports/filters/class'
import studentSubsidyTypeFilter from 'app/components/common/reports/filters/studentSubsidyType'
import detailsFilter from 'app/components/common/reports/filters/details'
import mailFilter from 'app/components/common/reports/filters/mail'
import studentFilter from 'app/components/common/reports/filters/student'
import employeeFilter from 'app/components/common/reports/filters/employee'
import orderTypeFilter from 'app/components/common/reports/filters/orderType'
import subsidyFilter from 'app/components/common/reports/filters/subsidy'
import sellTypeFilter from 'app/components/common/reports/filters/sellType'
import foodModeFilter from 'app/components/common/reports/filters/foodMode'
import useExtraText from 'app/components/common/reports/useExtraText'
import productionTypeFilter from 'app/components/common/reports/filters/productionType'
import saleTypeFilter from 'app/components/common/reports/filters/saleType'

import groupFilter, { flags as group } from './filters/group'
import groupComplexesSpecialFilter, { flags as groupSpecial } from './filters/groupComplexesSpecial'
import periodFilter, { flags as period } from './filters/period'
import accountFilter from './filters/account'
import sellTypeOnlyComplexFilter from './filters/sellTypeOnlyComplex'
import excludeOVZFilter from './filters/excludeOVZ'
import expandSubsidiesFilter from './filters/expandSubsidies'
import splitByDaysFilter, { flags as splitByDays } from './filters/splitByDays'

import styles from './ReportFilter.scss'

type Props = {
  loading?: boolean,
  onChangeSplitByDays?: (current: boolean) => void,
}

const ReportFilter = memo<Props>(({ loading, onChangeSplitByDays }: Props) => {
  const skipFirstRef = useRef(true)
  const [, current, , { resetAll, applyFilters }] = useFilterData()

  const splitByDaysChecked = splitByDays.splitByDaysChecked(current)

  useEffect(() => {
    if (skipFirstRef.current) {
      skipFirstRef.current = false
      return
    }
    if (onChangeSplitByDays) {
      onChangeSplitByDays(splitByDaysChecked)
    }
  }, [splitByDaysChecked])

  const foundCustomerWithType =
    customerSearch.customerFound(current) && customerSearch.foundCustomerHasType(current)

  const disabled = orgsOrUser.byUserSelected(current) && !foundCustomerWithType

  const extraText = useExtraText(
    period.finalDateInPreviosMonthOrBefore(current) &&
      (group.byGroupComplexesSelected(current) ||
        groupSpecial.byGroupComplexesSpecialSelected(current)),
  )

  const extraContent = extraText ? <PlainHelperText>{extraText}</PlainHelperText> : null

  return (
    <ReportSectionCSS
      header="Фильтры"
      stickyNumber={1}
      skipNextShift
      headerBar={<ResetFilterLink title="Сбросить все фильтры" onClick={resetAll} />}
    >
      {/* Окно с фильтром */}
      <RowLayoutContent paddingfree>
        <FiltersPlainLayout
          onApplyFilter={applyFilters}
          onResetAll={resetAll}
          loading={loading}
          disabled={disabled}
          extraContent={extraContent}
          skipHeader
        >
          <Column className={styles.column}>
            <FilterItem filterId={orgsOrUserFilter} />
            <FilterItem filterId={customerSearchFilter} />
            <FilterItem filterId={orgTypesFilter} />
            <FilterItem filterId={orgsFilter} />
            <FilterItem filterId={buildingFilter} />
            <FilterItem filterId={sellPointFilter} />
            <FilterItem filterId={customerTypeFilter} />
            <FilterItem filterId={classFilter} />
            <FilterItem filterId={studentFilter} />
            <FilterItem filterId={employeeFilter} />
            <FilterItem filterId={studentSubsidyTypeFilter} />
            <FilterItem filterId={detailsFilter} />
            <FilterItem filterId={groupFilter} />
            <FilterItem filterId={groupComplexesSpecialFilter} />
          </Column>
          <Column className={styles.column}>
            <FilterItem filterId={yearFilter} />
            <FilterItem filterId={periodFilter} />
            <FilterItem filterId={splitByDaysFilter} />
            <FilterItem filterId={orderTypeFilter} />
            <FilterItem filterId={subsidyFilter} />
            <FilterItem filterId={sellTypeFilter} />
            <FilterItem filterId={sellTypeOnlyComplexFilter} />
            <FilterItem filterId={excludeOVZFilter} />
            <FilterItem filterId={expandSubsidiesFilter} />
            <FilterItem filterId={foodModeFilter} />
            <FilterItem filterId={accountFilter} />
            <FilterItem filterId={productionTypeFilter} />
            <FilterItem filterId={saleTypeFilter} />
            <FilterItem filterId={mailFilter} />
          </Column>
        </FiltersPlainLayout>
      </RowLayoutContent>
    </ReportSectionCSS>
  )
})

export default ReportFilter
